import React from 'react';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import { Theme, Text } from '@bizzabo/design-system';

type TimestampType = Date | string;

const formatDistanceLocale = {
    xMinutes: 'm',
    xHours: 'h',
    xDays: 'd',
    xMonths: 'm',
    xYears: 'y',
};

const formatDistance = (token, count) =>
    token === 'xSeconds' ? 'Just now' : `${count}${formatDistanceLocale[token]}`;

const getDateOfTimeAgo = timestamp => format(timestamp, 'MMM. d', { locale: enUS });

const getTimeFromNow = (timestamp: TimestampType) => {
    if (!timestamp) {
        return null;
    }

    const time = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const timeAgo: string = formatDistanceToNowStrict(time, {
        locale: {
            formatDistance,
        },
    });
    return /[smh]/.test(timeAgo) ? timeAgo : getDateOfTimeAgo(timestamp);
};

export interface TimestampProps {
    startTime: TimestampType;
    className?: string;
    withDot?: boolean;
}

const Timestamp: React.FC<TimestampProps> = ({ startTime, className, withDot = false }) => {
    const classes = useStyles();
    const diffTime = getTimeFromNow(startTime);

    return (
        <Text
            variant="t81"
            nowrap
            className={cn(classes.timestamp, className, { [classes.withDot]: withDot })}
        >
            {diffTime}
        </Text>
    );
};

const useStyles = makeStyles(
    (theme: Theme) => ({
        timestamp: {
            color: theme.palette.greys[500],
            lineHeight: 1.6,
            marginRight: 4,
            minWidth: 'fit-content',
        },
        withDot: {
            position: 'relative',
            paddingLeft: 7,
            color: theme.palette.greys[500],
            '&::before': {
                content: "''",
                width: 3,
                height: 3,
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                background: theme.palette.greys[500],
                borderRadius: '50%',
            },
        },
    }),
    { classNamePrefix: Timestamp.name }
);

export default Timestamp;
